// Pure aggregation helpers over an events array (rows from the sk12_SiteActivity API).
// All rows come back PascalCase; the timestamp field is EventTimestampUtc.

function normalizeValue(value) {
  if (value === null || value === undefined || value === "") return "Unknown";
  return value;
}

/**
 * countBy(events, field) -> [{ key, count }] sorted by count desc.
 * null/undefined/"" field values are bucketed as "Unknown".
 */
export function countBy(events, field) {
  const counts = new Map();
  (events || []).forEach((ev) => {
    const key = normalizeValue(ev ? ev[field] : undefined);
    counts.set(key, (counts.get(key) || 0) + 1);
  });
  return Array.from(counts, ([key, count]) => ({ key, count })).sort(
    (a, b) => b.count - a.count
  );
}

/**
 * summarize(events) -> { totalViews, uniqueVisitors, uniqueSessions, topPage }.
 * uniqueVisitors = distinct VisitorId (fallback SessionId when VisitorId missing).
 * uniqueSessions = distinct SessionId.
 * topPage = most frequent Path ("—" if none).
 */
export function summarize(events) {
  const list = events || [];
  const visitors = new Set();
  const sessions = new Set();
  const pageCounts = new Map();

  list.forEach((ev) => {
    if (!ev) return;
    const visitor = ev.VisitorId || ev.SessionId;
    if (visitor) visitors.add(visitor);
    if (ev.SessionId) sessions.add(ev.SessionId);
    if (ev.Path) {
      pageCounts.set(ev.Path, (pageCounts.get(ev.Path) || 0) + 1);
    }
  });

  let topPage = "—";
  let topCount = 0;
  pageCounts.forEach((count, path) => {
    if (count > topCount) {
      topCount = count;
      topPage = path;
    }
  });

  return {
    totalViews: list.length,
    uniqueVisitors: visitors.size,
    uniqueSessions: sessions.size,
    topPage,
  };
}

/**
 * trendByDay(events) -> [{ date: "YYYY-MM-DD", count }] sorted date asc,
 * bucketed by the DATE part of EventTimestampUtc (fallback CreatedOn).
 * Rows with no timestamp are skipped.
 */
export function trendByDay(events) {
  const counts = new Map();
  (events || []).forEach((ev) => {
    if (!ev) return;
    const ts = ev.EventTimestampUtc || ev.CreatedOn;
    if (!ts) return;
    const date = String(ts).slice(0, 10);
    if (!date) return;
    counts.set(date, (counts.get(date) || 0) + 1);
  });
  return Array.from(counts, ([date, count]) => ({ date, count })).sort((a, b) =>
    a.date < b.date ? -1 : a.date > b.date ? 1 : 0
  );
}

/**
 * withinDays(events, days) -> events whose EventTimestampUtc is within the
 * last <days> days. If !days, return events unchanged. (App code — Date allowed.)
 */
export function withinDays(events, days) {
  if (!days) return events;
  const list = events || [];
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - days);
  const cutoffMs = cutoff.getTime();
  return list.filter((ev) => {
    if (!ev) return false;
    const ts = ev.EventTimestampUtc || ev.CreatedOn;
    if (!ts) return false;
    const t = new Date(ts).getTime();
    if (Number.isNaN(t)) return false;
    return t >= cutoffMs;
  });
}

/**
 * groupBySession(events) -> array of session objects, most-recently-ended first.
 * Each: { sessionId, visitorId, events (asc by time), count, start, end,
 *         durationMs, device, browser, os, state, city, country, entryPath, referrer }.
 */
export function groupBySession(events) {
  const map = new Map();
  (events || []).forEach((ev) => {
    if (!ev) return;
    const sid = ev.SessionId || "unknown";
    if (!map.has(sid)) map.set(sid, []);
    map.get(sid).push(ev);
  });

  const sessions = Array.from(map, ([sessionId, evs]) => {
    const sorted = evs
      .slice()
      .sort((a, b) =>
        String(a.EventTimestampUtc || "").localeCompare(String(b.EventTimestampUtc || ""))
      );
    const first = sorted[0] || {};
    const last = sorted[sorted.length - 1] || {};
    const startMs = new Date(first.EventTimestampUtc || 0).getTime();
    const endMs = new Date(last.EventTimestampUtc || 0).getTime();
    const durationMs =
      Number.isNaN(startMs) || Number.isNaN(endMs) ? 0 : Math.max(0, endMs - startMs);
    return {
      sessionId,
      visitorId: first.VisitorId || "",
      events: sorted,
      count: sorted.length,
      start: first.EventTimestampUtc || null,
      end: last.EventTimestampUtc || null,
      durationMs,
      device: first.DeviceType || "Unknown",
      browser: first.Browser || "Unknown",
      os: first.OS || "Unknown",
      state: first.State || "Unknown",
      city: first.City || "",
      country: first.Country || "",
      entryPath: first.Path || "",
      referrer: first.Referrer || "",
    };
  });

  return sessions.sort((a, b) => String(b.end || "").localeCompare(String(a.end || "")));
}

/**
 * entryPages(events) -> [{ key, count }] of the FIRST path of each session.
 */
export function entryPages(events) {
  const counts = new Map();
  groupBySession(events).forEach((s) => {
    const p = s.entryPath || "Unknown";
    counts.set(p, (counts.get(p) || 0) + 1);
  });
  return Array.from(counts, ([key, count]) => ({ key, count })).sort((a, b) => b.count - a.count);
}

/**
 * newVsReturning(events) -> [{key:"New",count},{key:"Returning",count}] by VisitorId
 * (a visitor with more than one session is "Returning").
 */
export function newVsReturning(events) {
  const perVisitor = new Map();
  groupBySession(events).forEach((s) => {
    const v = s.visitorId || s.sessionId;
    perVisitor.set(v, (perVisitor.get(v) || 0) + 1);
  });
  let fresh = 0;
  let returning = 0;
  perVisitor.forEach((n) => {
    if (n > 1) returning += 1;
    else fresh += 1;
  });
  return [
    { key: "New", count: fresh },
    { key: "Returning", count: returning },
  ];
}

/**
 * sessionStats(events) -> { sessions, avgPagesPerSession, avgDurationMs, bounceRate }.
 * bounceRate = share of single-page sessions.
 */
export function sessionStats(events) {
  const sessions = groupBySession(events);
  const n = sessions.length;
  if (n === 0) return { sessions: 0, avgPagesPerSession: 0, avgDurationMs: 0, bounceRate: 0 };
  const totalPages = sessions.reduce((s, x) => s + x.count, 0);
  const totalDuration = sessions.reduce((s, x) => s + (x.durationMs || 0), 0);
  const bounces = sessions.filter((x) => x.count <= 1).length;
  return {
    sessions: n,
    avgPagesPerSession: totalPages / n,
    avgDurationMs: totalDuration / n,
    bounceRate: bounces / n,
  };
}

/** formatDuration(ms) -> human string ("0s", "3m 12s", "1h 4m"). */
export function formatDuration(ms) {
  const total = Math.round((ms || 0) / 1000);
  if (total < 60) return `${total}s`;
  const m = Math.floor(total / 60);
  const s = total % 60;
  if (m < 60) return `${m}m ${s}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

/**
 * sessionsByDay(events) -> [{ date: "YYYY-MM-DD", count }] where count is the
 * number of DISTINCT sessions active that day. Sorted date asc.
 */
export function sessionsByDay(events) {
  const dayToSessions = new Map();
  (events || []).forEach((ev) => {
    if (!ev) return;
    const ts = ev.EventTimestampUtc || ev.CreatedOn;
    if (!ts) return;
    const date = String(ts).slice(0, 10);
    if (!date) return;
    if (!dayToSessions.has(date)) dayToSessions.set(date, new Set());
    if (ev.SessionId) dayToSessions.get(date).add(ev.SessionId);
  });
  return Array.from(dayToSessions, ([date, set]) => ({ date, count: set.size })).sort((a, b) =>
    a.date < b.date ? -1 : a.date > b.date ? 1 : 0
  );
}
